import { useTranslation } from 'react-i18next';
import type { JobRunSummaryDto } from '@/api/generated/schemas';
import { useNavigationContext } from '@/shared/navigation/react/useNavigationContext';
import { Badge, Button } from '@/shared/ui';
import { cn } from '@/shared/lib/utils';
import { JobSegmentedBar } from '../../internal/JobSegmentedBar';
import { JobStatusIcon } from '../../internal/JobStatusIcon';
import { formatDuration, formatThousands } from '../../internal/jobsFormat';
import {
  countsDone,
  runVisualStatus,
  segmentsFromCounts,
  type JobVisualStatus,
} from '../../internal/jobsModel';
import { getJobResultActions, toJobResultActionContext } from '../../jobResultActions';

const BADGE_TONE: Record<JobVisualStatus, 'neutral' | 'info' | 'success' | 'danger'> = {
  queued: 'neutral',
  running: 'info',
  done: 'success',
  error: 'danger',
};

interface JobOverviewRowProps {
  run: JobRunSummaryDto;
  compact: boolean;
  now: number;
  onOpenDetail: (runId: string) => void;
}

/**
 * Řádek jobu v centrálním přehledu (dle designu): stavová ikona, titulek (2 řádky), stavový
 * badge, segmentovaný bar, meta s během + aktuálním krokem, výsledkové akce a „Detail".
 * Kompaktní varianta (dokončeno mimo pozornost): bez baru a prokliku, jen shrnutí.
 */
export function JobOverviewRow({ run, compact, now, onOpenDetail }: JobOverviewRowProps) {
  const { t } = useTranslation('jobs');
  const { kernel } = useNavigationContext();
  const visual = runVisualStatus(run);
  const durationMs =
    (run.completedAt ? Date.parse(run.completedAt) : now) - Date.parse(run.createdAt);
  const actions = run.status === 'Succeeded' ? getJobResultActions(run.jobType) : [];

  return (
    <div
      data-testid="job-overview-row"
      title={t('central.startedBy', { name: run.initiatedByUserName })}
      onClick={compact ? undefined : () => onOpenDetail(run.id)}
      className={cn(
        'border-phx-border flex flex-col gap-[7px] border-b px-[14px] pt-[11px] pb-3',
        !compact && 'hover:bg-phx-surface-2 cursor-pointer'
      )}
    >
      <div className="flex items-start gap-[9px]">
        <span className="mt-px inline-flex h-5 w-5 shrink-0 items-center justify-center">
          <JobStatusIcon state={visual} size={visual === 'running' ? 15 : 16} />
        </span>
        <span className="text-phx-text line-clamp-2 min-w-0 flex-1 text-[13px] leading-[1.4] font-semibold">
          {run.title}
        </span>
        <Badge tone={BADGE_TONE[visual]} appearance="soft" className="shrink-0">
          {t(`status.${visual}`)}
        </Badge>
      </div>

      {!compact ? (
        <JobSegmentedBar segments={segmentsFromCounts(run.counts)} className="h-[6px]" />
      ) : null}

      <div className="flex min-w-0 items-center gap-2">
        <span className="text-phx-text-muted shrink-0 text-[11.5px] tabular-nums">
          {compact
            ? t('central.metaCompact', {
                count: run.counts.total,
                duration: formatDuration(durationMs),
              })
            : t('central.meta', {
                done: formatThousands(countsDone(run.counts)),
                total: formatThousands(run.counts.total),
                duration: formatDuration(durationMs),
              })}
        </span>
        {!compact && run.currentStepName ? (
          <span className="text-phx-text-2 min-w-0 flex-1 truncate text-[11.5px]">
            · {t('central.running', { step: run.currentStepName })}
          </span>
        ) : null}
        <span className="flex-1" />
        {actions.map((action) => (
          <Button
            key={action.key}
            size="sm"
            className="shrink-0"
            onClick={(e) => {
              e.stopPropagation();
              action.onActivate(toJobResultActionContext(run, kernel));
            }}
          >
            {t(action.labelKey)}
          </Button>
        ))}
        {!compact ? (
          <span className="text-phx-link shrink-0 text-[11.5px]">{t('central.detail')}</span>
        ) : null}
      </div>
    </div>
  );
}
